import { Schema } from 'effect';
import { IncidentTrigger, NotificationMethod } from '../config/incident.js';
import { SiteId } from './common.js';
/**
 * DTO schemas for the /admin/config group: the write side of the documents
 * under `sites/{site}/config`. See http/payment.ts for conventions.
 *
 * A body here is deliberately stricter than the stored-document schema it ends
 * up in. That one has to read whatever is already in Firestore, so every field
 * is optional with a default; a write has no such history to honour, so every
 * field is required and bounded.
 */
export const SiteParams = Schema.Struct({ siteId: SiteId });
export const OkResponse = Schema.Struct({ message: Schema.String });
// --- PUT /admin/config/:siteId/incident --------------------------------------
const Recipient = Schema.Trim.pipe(Schema.check(Schema.isMaxLength(254)), Schema.check(Schema.isPattern(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)));
export const NotificationSettingsBody = Schema.Struct({
    uuid: Schema.String.pipe(Schema.check(Schema.isMinLength(1)), Schema.check(Schema.isMaxLength(64))),
    enabled: Schema.Boolean,
    method: Schema.Enum(NotificationMethod),
    recipients: Schema.Array(Recipient).pipe(Schema.check(Schema.isMaxLength(50))),
    incidentTriggers: Schema.Array(Schema.Enum(IncidentTrigger))
});
export const IncidentConfigBody = Schema.Struct({
    notifications: Schema.Array(NotificationSettingsBody).pipe(Schema.check(Schema.isMaxLength(50)))
});
// --- PUT /admin/config/:siteId/lights ----------------------------------------
const Id = Schema.String.pipe(Schema.check(Schema.isMinLength(1)), Schema.check(Schema.isMaxLength(64)));
const Name = Schema.String.pipe(Schema.check(Schema.isMaxLength(100)));
const Minutes = (max) => Schema.Int.pipe(Schema.check(Schema.isBetween({ minimum: 0, maximum: max })));
/** 'MM-DD', or '' for no bound. The page normalizes on blur and on save. */
const MonthDay = Schema.String.pipe(Schema.check(Schema.isPattern(/^((0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01]))?$/)));
export const LightControllerBody = Schema.Struct({
    id: Id,
    name: Name,
    type: Schema.Literals(['shelly', 'gateway']),
    model: Name,
    channels: Schema.Int.pipe(Schema.check(Schema.isBetween({ minimum: 1, maximum: 64 }))),
    firmwareVersion: Name
});
/**
 * A zone as the page edits it. `manualOnUntil` is not part of it: the manual
 * override has its own endpoint (D77), and the save keeps the stored value so
 * a page opened before an override cannot cancel it.
 */
export const LightZoneBody = Schema.Struct({
    id: Id,
    name: Name,
    controllerId: Id,
    channel: Schema.Int.pipe(Schema.check(Schema.isBetween({ minimum: 0, maximum: 63 }))),
    spaceIds: Schema.Array(Id).pipe(Schema.check(Schema.isMaxLength(100))),
    leadMinutes: Minutes(24 * 60),
    trailMinutes: Minutes(24 * 60),
    maxOnMinutes: Minutes(7 * 24 * 60),
    enabled: Schema.Boolean,
    seasonOffFrom: MonthDay,
    seasonOffTo: MonthDay
});
export const LightsConfigBody = Schema.Struct({
    controllers: Schema.Array(LightControllerBody).pipe(Schema.check(Schema.isMaxLength(50))),
    zones: Schema.Array(LightZoneBody).pipe(Schema.check(Schema.isMaxLength(200)))
});
// --- PUT /admin/config/:siteId/codes/{club,access} ---------------------------
//
// config/codes holds fields owned by two pages: the Klubbkoder page writes
// `clubCodes`, the Adgang page `accessCodes` and `accessPointInstructions`.
// Each has its own endpoint, which replaces only its fields.
const Text = (max) => Schema.String.pipe(Schema.check(Schema.isMaxLength(max)));
export const ClubCodeBody = Schema.Struct({
    /** Stable and unique, 'club-000000000007'. Not the order. */
    id: Id,
    name: Name,
    slug: Name,
    code: Text(64),
    usage: Schema.Struct({
        eventDiscount: Schema.Boolean,
        eventSignup: Schema.Boolean,
        hourBooking: Schema.Boolean,
        hourDiscount: Schema.Boolean,
        seasonTicket: Schema.Boolean
    }),
    lockedUsers: Text(10_000),
    lockedItems: Text(5_000),
    spaceTime: Text(2_000),
    validTo: Text(40),
    validFrom: Text(40),
    freeText: Text(2_000),
    generalDiscount: Schema.optionalKey(Schema.Struct({
        rentalDiscount: Schema.optionalKey(Schema.Struct({
            type: Schema.Literals(['percentage', 'fixed']),
            value: Schema.Finite.pipe(Schema.check(Schema.isBetween({ minimum: 0, maximum: 1_000_000 })))
        }))
    }))
});
export const ClubCodesBody = Schema.Struct({
    clubCodes: Schema.Array(ClubCodeBody).pipe(Schema.check(Schema.isMaxLength(200)))
});
export const AccessCodeBody = Schema.Struct({
    /** The order codes are evaluated in. */
    id: Schema.Int.pipe(Schema.check(Schema.isGreaterThanOrEqualTo(0))),
    code: Text(64),
    spaceTime: Text(2_000),
    validFrom: Text(40),
    validTo: Text(40),
    freeText: Text(2_000),
    accessPointId: Schema.optionalKey(Id)
});
export const AccessCodesBody = Schema.Struct({
    accessCodes: Schema.Array(AccessCodeBody).pipe(Schema.check(Schema.isMaxLength(200))),
    /** Instruksjoner (eksternt) per access point id; replaces the whole map. */
    accessPointInstructions: Schema.Record(Id, Text(5_000))
});
//# sourceMappingURL=config.js.map